<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import { useRoute } from 'vue-router'
import { filter, intersection, map, replace, size } from 'lodash'
import SIDE_NAV from '@/constants/nav/side'
import { useMe } from '@/queries/me'
import SideNavMobile from '@/layouts/components/SideNavMobile.vue'
import SideNavMenu from '@/layouts/components/SideNavMenu.vue'
import type { SideNavItem, SideNavLocation } from '@/types/sideNav'

const route = useRoute()
const { data: me, isPaid } = useMe()

const features = computed(() => me.value?.features ?? [])
const roles = computed(() => me.value?.roles ?? [])

const props = defineProps({
    menu: {
        type: String as PropType<SideNavLocation>,
        required: true
    }
})

const filterRolesFeaturesAndPaid = computed(() => {
    const userRoles = roles.value
    const userFeatures = features.value
    const orgIsPaid = isPaid.value

    return filter(SIDE_NAV[props.menu], (item: SideNavItem) => {
        const positiveFeatures = item.features ? item.features.filter(feature => !feature.startsWith('!')) : []
        const negativeFeatures = item.features
            ? item.features.filter(feature => feature.startsWith('!')).map(feature => feature.substring(1))
            : []

        const hasRequireRoles = !item.roles || Boolean(size(intersection(item.roles, userRoles)))
        const hasRequireFeatures =
            !positiveFeatures.length || Boolean(size(intersection(positiveFeatures, userFeatures)))
        const hasNoNegativeFeatures = !negativeFeatures.length || !size(intersection(negativeFeatures, userFeatures))

        const isPaid = !item.isPaid || orgIsPaid === item.isPaid

        return hasRequireRoles && hasRequireFeatures && hasNoNegativeFeatures && isPaid
    })
})

const sideNav = computed(() =>
    map(filterRolesFeaturesAndPaid.value, item => {
        switch (props.menu) {
            case `events`:
                return {
                    ...item,
                    route: replace(item.route, `{instanceId}`, (route.params?.instanceId as string) || ``)
                }
            case `person`:
                return {
                    ...item,
                    route: replace(item.route, `{personId}`, (route.params?.personId as string) || ``)
                }
            default:
                return item
        }
    })
)
</script>

<template>
    <SideNavMobile :nav="sideNav" />
    <SideNavMenu :nav="sideNav" />
</template>
